import React, { useEffect, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import {
  listStockPendingActions,
  resolveStockPendingAction,
} from "../../services/invoicingServices";

// Banner+modal para mostrar pending stock actions (notas de crédito,
// facturas apagadas) que precisam de decisão do user. Não repõe stock
// automaticamente — o user resolve cada uma: repor / não repor / parcial.
const StockAlertsBanner = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [partialQty, setPartialQty] = useState({});

  const load = async () => {
    try {
      setLoading(true);
      const r = await listStockPendingActions("pending");
      setActions(r?.data?.data?.rows || []);
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resolve = async (action, resolution, qty = null) => {
    if (resolution === "partial" && (!qty || qty <= 0)) {
      toast.error("Indica uma quantidade válida");
      return;
    }
    const isBaixa = action.action_type === "invoice_external_baixa";
    if (resolution === "skip") {
      const ok = window.confirm(
        isBaixa
          ? "Ignorar esta factura externa? O stock TP NÃO será baixado."
          : "Marcar como 'não repor stock'? Isto significa que as unidades vendidas continuam fora do inventário (ex: peças danificadas).",
      );
      if (!ok) return;
    }
    try {
      setResolving(action.id);
      const r = await resolveStockPendingAction(action.id, {
        resolution,
        qty,
      });
      const data = r?.data?.data;
      toast.success(
        resolution === "skip"
          ? "Marcado como ignorar"
          : isBaixa
          ? `${data?.restored || 0} unidade${data?.restored === 1 ? "" : "s"} - baixa stock`
          : `${data?.restored || 0} unidade${data?.restored === 1 ? "" : "s"} repostas`,
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setResolving(null);
    }
  };

  if (!actions.length) return null;

  return (
    <>
      <div
        style={{
          backgroundColor: "#f8d7da",
          border: "1px solid #f5c2c7",
          color: "#842029",
          padding: 10,
          borderRadius: 6,
          marginBottom: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <strong>
            <i className="bi bi-exclamation-triangle-fill"></i>{" "}
            {actions.length} acção{actions.length === 1 ? "" : "ões"} pendente
            {actions.length === 1 ? "" : "s"} de stock
          </strong>{" "}
          — facturas apagadas ou notas de crédito a precisar de decisão.
        </div>
        <button
          type="button"
          className="btn btn-sm btn-danger"
          onClick={() => setOpen(true)}
        >
          Ver / resolver
        </button>
      </div>

      <Modal isOpen={open} toggle={() => setOpen(false)} size="lg">
        <ModalHeader toggle={() => setOpen(false)}>
          Acções pendentes de stock
        </ModalHeader>
        <ModalBody>
          <p style={{ fontSize: 13, color: "#6c757d" }}>
            Para cada linha decide: <strong>Repor stock</strong> (peça volta ao
            inventário), <strong>Repor parcial</strong> (só N unidades, ex:
            quando a nota de crédito é parcial), ou{" "}
            <strong>Não repor</strong> (peça danificada/perdida).
          </p>
          {loading && <div>A carregar...</div>}
          {actions.map((a) => {
            const labelIds = (() => {
              try {
                return typeof a.candidate_label_ids === "string"
                  ? JSON.parse(a.candidate_label_ids)
                  : a.candidate_label_ids || [];
              } catch (_) {
                return [];
              }
            })();
            const isResolving = resolving === a.id;
            return (
              <div
                key={a.id}
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                  padding: 12,
                  marginBottom: 10,
                  background: "#fff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 13,
                  }}
                >
                  <div>
                    <strong>
                      {a.document_label || `Factura #${a.invoice_number || a.document_id}`}
                    </strong>
                    {a.entity_name && <> — {a.entity_name}</>}
                    {a.entity_vat && (
                      <small className="text-muted"> · NIF {a.entity_vat}</small>
                    )}
                  </div>
                  <span
                    className="badge bg-secondary"
                    style={{ fontSize: 10, alignSelf: "center" }}
                  >
                    {a.action_type === "credit_note_emitted"
                      ? "Nota de crédito"
                      : a.action_type === "credit_note_partial"
                      ? "Nota de crédito parcial"
                      : a.action_type === "invoice_deleted"
                      ? "Documento apagado"
                      : a.action_type === "invoice_canceled"
                      ? "Documento cancelado"
                      : a.action_type === "invoice_finalized_externally"
                      ? "Rascunho finalizado no Moloni"
                      : a.action_type}
                  </span>
                </div>
                <div style={{ marginTop: 6, fontSize: 13 }}>
                  Peça:{" "}
                  <strong>
                    {a.parts_internal_id ? `#${a.parts_internal_id}` : ""}{" "}
                    {a.part_name || `parts_id=${a.parts_id}`}
                  </strong>
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: "#6c757d" }}>
                  Pedido: <strong>{a.requested_qty}</strong> · Disponível para
                  reposição: <strong>{a.available_qty}</strong> unidades
                </div>
                <div
                  style={{
                    marginTop: 10,
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={() => resolve(a, "restore")}
                    disabled={isResolving || a.available_qty === 0}
                  >
                    <i className="bi bi-arrow-counterclockwise"></i> Repor
                    todas ({a.available_qty})
                  </button>
                  <div style={{ display: "flex", gap: 4 }}>
                    <input
                      type="number"
                      min={1}
                      max={a.available_qty}
                      placeholder="N"
                      style={{ width: 70 }}
                      className="form-control form-control-sm"
                      value={partialQty[a.id] ?? ""}
                      onChange={(e) =>
                        setPartialQty((p) => ({
                          ...p,
                          [a.id]: e.target.value,
                        }))
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-warning"
                      onClick={() =>
                        resolve(a, "partial", Number(partialQty[a.id]))
                      }
                      disabled={isResolving}
                    >
                      Repor parcial
                    </button>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => resolve(a, "skip")}
                    disabled={isResolving}
                  >
                    Não repor
                  </button>
                </div>
              </div>
            );
          })}
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setOpen(false)}
          >
            Fechar
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default StockAlertsBanner;
